import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, AlertCircle, BookOpen, Plus, Clock, Bell } from 'lucide-react';
import { getTasks, saveTasks, getSubjects } from '../utils/storage';

const AddTask = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subjectId: '',
    deadline: '',
    time: '',
    reminderTime: '',
    priority: 'medium'
  });
  const [subjects, setSubjects] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const subj = getSubjects();
    setSubjects(subj);
    setFormData(prev => ({
      ...prev,
      subjectId: subj.length > 0 ? subj[0].id : '',
      deadline: format(new Date(), 'yyyy-MM-dd') // Default to today
    }));
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Task title is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Task title must be at least 3 characters';
    }

    if (!formData.subjectId) {
      newErrors.subjectId = 'Please select a subject';
    }

    if (!formData.deadline) {
      newErrors.deadline = 'Please set a deadline';
    } else {
      const selectedDate = new Date(formData.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.deadline = 'Deadline cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const tasks = getTasks();
    const newTask = {
      id: Date.now().toString(),
      subjectId: formData.subjectId,
      title: formData.title.trim(),
      description: formData.description.trim(),
      deadline: formData.deadline,
      time: formData.time,
      reminderTime: formData.reminderTime,
      priority: formData.priority,
      completed: false,
      createdAt: new Date().toISOString()
    };

    tasks.push(newTask);
    saveTasks(tasks);
    navigate('/');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#64748b';
    }
  };

  const isFormValid = formData.title.trim() && formData.subjectId && formData.deadline;

  return (
    <div className="container">
      <div className="form-header">
        <h1>Add New Task</h1>
        <p className="form-subtitle">Create a focused task to stay on track with your studies (defaults to today)</p>
      </div>

      <div className="form-card">
        <div className="form-group">
          <label htmlFor="task-title" className="form-label">
            Task Title *
          </label>
          <div className="input-wrapper">
            <input
              id="task-title"
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Complete math homework chapter 5"
              className={`input ${errors.title ? 'error' : ''}`}
              maxLength={100}
            />
            {errors.title && (
              <div className="error-message">
                <AlertCircle size={16} />
                {errors.title}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="task-description" className="form-label">
            Description (Optional)
          </label>
          <textarea
            id="task-description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Add more details about this task..."
            className="textarea"
            rows={3}
            maxLength={300}
          />
          <div className="char-count">
            {formData.description.length}/300
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="subject-select" className="form-label">
              Subject *
            </label>
            <div className="select-wrapper">
              <BookOpen size={16} className="select-icon" />
              <select
                id="subject-select"
                value={formData.subjectId}
                onChange={(e) => handleInputChange('subjectId', e.target.value)}
                className={`select ${errors.subjectId ? 'error' : ''}`}
              >
                <option value="">Select a subject</option>
                {subjects.map(subj => (
                  <option key={subj.id} value={subj.id}>{subj.name}</option>
                ))}
              </select>
            </div>
            {errors.subjectId && (
              <div className="error-message">
                <AlertCircle size={16} />
                {errors.subjectId}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="priority-select" className="form-label">
              Priority
            </label>
            <div className="select-wrapper">
              <div
                className="priority-dot"
                style={{ backgroundColor: getPriorityColor(formData.priority) }}
              />
              <select
                id="priority-select"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="select"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="deadline-date" className="form-label">
              Deadline *
            </label>
            <div className="input-wrapper">
              <Calendar size={16} className="input-icon" />
              <input
                id="deadline-date"
                type="date"
                value={formData.deadline}
                onChange={(e) => handleInputChange('deadline', e.target.value)}
                className={`input ${errors.deadline ? 'error' : ''}`}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
            {errors.deadline && (
              <div className="error-message">
                <AlertCircle size={16} />
                {errors.deadline}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="deadline-time" className="form-label">
              Time (Optional)
            </label>
            <div className="input-wrapper">
              <Clock size={16} className="input-icon" />
              <input
                id="deadline-time"
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                className="input"
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="reminder-time" className="form-label">
            Reminder Time (Optional)
          </label>
          <div className="input-wrapper">
            <Bell size={16} className="input-icon" />
            <input
              id="reminder-time"
              type="time"
              value={formData.reminderTime}
              onChange={(e) => handleInputChange('reminderTime', e.target.value)}
              className="input"
              placeholder="Set reminder time"
            />
          </div>
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
            Choose when you want to be reminded about this task
          </small>
        </div>

        <div className="form-actions">
          <button
            onClick={() => navigate('/')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="save-button"
            disabled={!isFormValid}
          >
            <Plus size={20} />
            Create Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTask;
